// Client-side validation and input normalization utilities
// Handles Persian/Arabic digit conversion and various input validations
// For UX only - backend MUST validate

export function useInputValidation() {
  // Persian/Farsi digit mappings
  const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
  const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";
  const ENGLISH_DIGITS = "0123456789";

  /**
   * Convert Persian/Arabic digits to English digits
   */
  function normalizeDigits(input: string): string {
    return input
      .split("")
      .map((char) => {
        const persianIndex = PERSIAN_DIGITS.indexOf(char);
        if (persianIndex !== -1) return ENGLISH_DIGITS[persianIndex];

        const arabicIndex = ARABIC_DIGITS.indexOf(char);
        if (arabicIndex !== -1) return ENGLISH_DIGITS[arabicIndex];

        return char;
      })
      .join("");
  }

  // Iranian mobile number validation: 09XXXXXXXXX (11 digits)
  // Accepts English, Persian, and Arabic digits
  const MOBILE_REGEX = /^09[0-9۰-۹٠-٩]{9}$/;
  const OTP_REGEX = /^[0-9۰-۹٠-٩]{6}$/;

  /**
   * Validate Iranian mobile number format
   */
  function validateMobileNumber(mobile: string): { valid: boolean; error?: string } {
    if (!mobile) {
      return { valid: false, error: "Mobile number is required" };
    }

    // Remove any spaces or formatting
    const cleaned = mobile.replace(/\s+/g, "");

    if (!MOBILE_REGEX.test(cleaned)) {
      return { valid: false, error: "Please enter a valid mobile number (09XXXXXXXXX)" };
    }

    return { valid: true };
  }

  /**
   * Validate OTP code format
   */
  function validateOtp(otp: string): { valid: boolean; error?: string } {
    if (!otp) {
      return { valid: false, error: "OTP code is required" };
    }

    const cleaned = otp.replace(/\s+/g, "");

    if (!OTP_REGEX.test(cleaned)) {
      return { valid: false, error: "OTP must be 6 digits" };
    }

    return { valid: true };
  }

  /**
   * Validate password strength (client-side basic check)
   */
  function validatePassword(password: string): { valid: boolean; error?: string } {
    if (!password) {
      return { valid: false, error: "Password is required" };
    }

    if (password.length < 8) {
      return { valid: false, error: "Password must be at least 8 characters" };
    }

    if (password.length > 64) {
      return { valid: false, error: "Password must not exceed 64 characters" };
    }

    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUppercase || !hasLowercase || !hasDigit || !hasSpecial) {
      return {
        valid: false,
        error: "Password must contain uppercase, lowercase, digit, and special character",
      };
    }

    return { valid: true };
  }

  /**
   * Validate email format
   */
  function validateEmail(email: string): { valid: boolean; error?: string } {
    if (!email) {
      return { valid: false, error: "Email is required" };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      return { valid: false, error: "Please enter a valid email address" };
    }

    if (email.length > 254) {
      return { valid: false, error: "Email is too long" };
    }

    return { valid: true };
  }

  /**
   * Validate name (first name, last name)
   */
  function validateName(name: string, fieldName = "Name"): { valid: boolean; error?: string } {
    if (!name) {
      return { valid: false, error: `${fieldName} is required` };
    }

    if (name.length < 2) {
      return { valid: false, error: `${fieldName} must be at least 2 characters` };
    }

    if (name.length > 64) {
      return { valid: false, error: `${fieldName} must not exceed 64 characters` };
    }

    // Allow unicode letters, spaces, hyphens, apostrophes
    const nameRegex = /^[\p{L}\s'-]+$/u;

    if (!nameRegex.test(name)) {
      return { valid: false, error: `${fieldName} contains invalid characters` };
    }

    return { valid: true };
  }

  /**
   * Sanitize mobile number (remove formatting, keep only digits)
   * Converts Persian/Arabic digits to English digits
   */
  function sanitizeMobileNumber(mobile: string): string {
    // First normalize Persian/Arabic digits to English
    const normalized = normalizeDigits(mobile);
    // Then remove all non-digit characters
    return normalized.replace(/\D/g, "");
  }

  /**
   * Format mobile number for display: 0912 345 6789
   */
  function formatMobileNumber(mobile: string): string {
    const cleaned = sanitizeMobileNumber(mobile);
    if (cleaned.length !== 11) return cleaned;
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  }

  /**
   * Mask mobile number: 0912•••6789
   * Wrapped with LTR marks (U+202A/U+202C) for proper RTL display
   */
  function maskMobileNumber(mobile: string): string {
    const cleaned = sanitizeMobileNumber(mobile);
    if (cleaned.length !== 11) return cleaned;
    const masked = `${cleaned.slice(0, 4)}•••${cleaned.slice(-4)}`;
    // Wrap with Left-to-Right Embedding (U+202A) and Pop Directional Formatting (U+202C)
    return `\u202A${masked}\u202C`;
  }

  return {
    validateMobileNumber,
    validateOtp,
    validatePassword,
    validateEmail,
    validateName,
    sanitizeMobileNumber,
    formatMobileNumber,
    maskMobileNumber,
    normalizeDigits, // Export for use in any component that needs digit normalization
  };
}
